import { createBrowserRouter, Navigate, useLocation, type RouteObject } from 'react-router-dom'

import { tokenStore } from '@/lib/api/tokenStore'
import { AccountPasswordResetPage, AccountSignupGuidePage } from '@/pages/AccountHelpPage'
import { LoginPage } from '@/pages/LoginPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { VenueBookingDetailPage } from '@/pages/VenueBookingDetailPage'
import { VenueBookingsCalendarPage } from '@/pages/VenueBookingsCalendarPage'
import { VenueBookingsListPage } from '@/pages/VenueBookingsListPage'
import { VenueBusinessInfoEditPage } from '@/pages/VenueBusinessInfoEditPage'
import { VenueBusinessInfoPage } from '@/pages/VenueBusinessInfoPage'
import { VenueClosuresPage } from '@/pages/VenueClosuresPage'
import { VenueCourtOperatingHoursPage } from '@/pages/VenueCourtOperatingHoursPage'
import { VenueCourtPricingPage } from '@/pages/VenueCourtPricingPage'
import { VenueCourtsPage } from '@/pages/VenueCourtsPage'
import { VenueDashboardPage } from '@/pages/VenueDashboardPage'
import { VenueHomePage } from '@/pages/VenueHomePage'
import { VenueNoticeComposePage } from '@/pages/VenueNoticeComposePage'
import { VenueNoticeDetailPage } from '@/pages/VenueNoticeDetailPage'
import { VenueNoticeHistoryPage } from '@/pages/VenueNoticeHistoryPage'
import { VenueNoticesHomePage } from '@/pages/VenueNoticesHomePage'
import { VenueOperationVerificationExpiryNoticePage } from '@/pages/VenueOperationVerificationExpiryNoticePage'
import { VenueOperationVerificationHistoryPage } from '@/pages/VenueOperationVerificationHistoryPage'
import { VenueOperationVerificationPage } from '@/pages/VenueOperationVerificationPage'
import { VenueOperationVerificationStatusPage } from '@/pages/VenueOperationVerificationStatusPage'
import { VenueProfileEditPage } from '@/pages/VenueProfileEditPage'
import { VenueProfileSummaryPage } from '@/pages/VenueProfileSummaryPage'
import { VenuePromotionComposePage } from '@/pages/VenuePromotionComposePage'
import { VenuePromotionPerformancePage } from '@/pages/VenuePromotionPerformancePage'
import { VenueRegisterCompletePage } from '@/pages/VenueRegisterCompletePage'
import { VenueRegisterPage } from '@/pages/VenueRegisterPage'
import { VenueReservationSettingPage } from '@/pages/VenueReservationSettingPage'
import { VenueSalesCyclesPage } from '@/pages/VenueSalesCyclesPage'
import { VenueSalesDeductionsPage } from '@/pages/VenueSalesDeductionsPage'
import { VenueSalesHomePage } from '@/pages/VenueSalesHomePage'
import { VenueSalesRefundsPage } from '@/pages/VenueSalesRefundsPage'
import { VenueSalesRevenuePage } from '@/pages/VenueSalesRevenuePage'
import { VenueSelectPage } from '@/pages/VenueSelectPage'

import { GuestRoute } from './GuestRoute'
import { resolvePostLoginPath } from './postLogin'
import { ProtectedRoute } from './ProtectedRoute'
import { RootLayout } from './RootLayout'
import { RouteError } from './RouteError'
import { VenueLayout } from './VenueLayout'

/** `/` — 세션이 있으면 마지막 구장으로, 없으면 로그인으로. */
function RootRedirect() {
  const location = useLocation()

  if (!tokenStore.hasValidSession()) return <Navigate to="/login" replace />
  return <Navigate to={resolvePostLoginPath(location.search)} replace />
}

/**
 * 구장 하위 화면들.
 *
 * 🚨 **후속 화면은 여기 항목 1개 + `pages/XxxPage.tsx` + `navItems.ts` 플래그 전환이 전부다.**
 * `path` 문자열은 `navItems.ts` 의 `path` 와 반드시 같아야 한다(동기화 테스트가 강제).
 * `handle.title` 은 셸 톱바가 읽는다(설계 D7).
 */
const venueChildren: RouteObject[] = [
  { index: true, element: <VenueHomePage />, handle: { title: '구장 관리' } },
  // 🚨 대시보드는 셸 레벨 노출 배너(`VenueExposureBanner`)를 억제한다 — 화면 자체가 이미 운영 인증
  // 상태 요약을 보여주므로 중복 배너를 띄우지 않는다(설계 §2 자율 결정 D2 · #592 자율 결정 6).
  {
    path: 'dashboard',
    element: <VenueDashboardPage />,
    handle: { title: '대시보드', suppressExposureBanner: true },
  },
  // 🚨 #778 — 이 화면에도 운영 인증 요약 카드(배너 포함, `badge` 5상태 기준)를 얹어 셸 배너
  // (`VenueExposureBanner`, `EXPIRED` 축만 봄)와 내용이 겹친다 — 대시보드와 같은 이유로 억제한다
  // (설계 §2 자율 결정 1, 기존 동작 변경).
  {
    path: 'venue',
    element: <VenueProfileSummaryPage />,
    handle: { title: '구장 정보·코트 설정', suppressExposureBanner: true },
  },
  {
    path: 'venue/edit',
    element: <VenueProfileEditPage />,
    handle: { title: '구장 기본정보 편집' },
  },
  // 🚨 #772 — 운영 인증 신청·서류 계약(백엔드 #771)이 확정되어 신청 화면을 연다. `navItems` 미등록 —
  // `venue/edit`과 같은 관례로 대시보드·구장정보 요약 카드 2곳에서만 진입한다(설계 §2 자율 결정 2).
  {
    path: 'operation-verification',
    element: <VenueOperationVerificationPage />,
    handle: { title: '운영 인증 신청' },
  },
  // 🚨 #778 — 운영 인증 상태 요약(EP-A)·이력(EP-2) 조회 계약(백엔드 #770·#777)이 확정되어 완료·실패·
  // 미완료 안내 화면을 연다. `navItems` 미등록 — 요약 카드의 "자세히 보기"에서만 진입한다(설계 §2
  // 자율 결정 2·9). 자체 상태 요약 화면이라 셸 배너를 억제한다(설계 §2 자율 결정 1).
  {
    path: 'operation-verification/status',
    element: <VenueOperationVerificationStatusPage />,
    handle: { title: '운영 인증 현황', suppressExposureBanner: true },
  },
  {
    path: 'operation-verification/history',
    element: <VenueOperationVerificationHistoryPage />,
    handle: { title: '운영 인증 이력' },
  },
  // 🚨 #782 — 대시보드 "우선 처리 항목"(actionType=OPERATION_VERIFICATION)의 유일한 진입점. `navItems`
  // 미등록 — `operation-verification/status`와 같은 관례. 자체 상태 안내 화면이라 셸 배너를 억제한다
  // (설계 §2 자율 결정 1과 같은 이유 — 새 화면의 안내와 셸 배너가 중복되지 않게).
  {
    path: 'operation-verification/expiry-notice',
    element: <VenueOperationVerificationExpiryNoticePage />,
    handle: { title: '운영 인증 기한 알림', suppressExposureBanner: true },
  },
  // 🚨 #1191 — 사업자 정보 수정 요청·현황 계약(백엔드 #1189 §D7)이 확정되어 화면을 연다. 운영 인증
  // 신청 축(`operation-verification/*`)과 별개 축이다(design.md §2). `navItems` 미등록 — 같은 화면
  // 안에서만 진입한다: `OperationVerificationSummaryCard`의 "사업자 정보 수정" 버튼 → 현황(`business-info`)
  // → "정보 수정" 버튼 → 폼(`business-info/edit`).
  {
    path: 'business-info',
    element: <VenueBusinessInfoPage />,
    handle: { title: '사업자 정보' },
  },
  {
    path: 'business-info/edit',
    element: <VenueBusinessInfoEditPage />,
    handle: { title: '사업자 정보 수정' },
  },
  // 🚨 #598 — 코트 CRUD 정본 계약(#597)이 확정되어 플레이스홀더를 실제 화면으로 교체했다.
  {
    path: 'courts',
    element: <VenueCourtsPage />,
    handle: { title: '코트 목록 관리' },
  },
  // 카드의 '운영시간 설정' 액션이 연결하는 곳(`#600` 백엔드 계약 확정, `#601` 이 실제 화면으로 교체).
  // `navItems` 미등록 — `venue/edit` 처럼 사이드바 밖 하위 액션 라우트.
  {
    path: 'courts/:courtId/operating-hours',
    element: <VenueCourtOperatingHoursPage />,
    handle: { title: '운영시간 설정' },
  },
  // 🚨 #868 — 시간대별 요금(백엔드 #852)·날짜 지정 요금(#866) 계약이 확정되어 요금 설정 화면을 연다.
  // 운영시간 화면과 형제 라우트이며 진입점도 같다(코트 카드의 액션 링크). `navItems` 미등록.
  {
    path: 'courts/:courtId/pricing',
    element: <VenueCourtPricingPage />,
    handle: { title: '요금 설정' },
  },
  // 🚨 #604 — 휴무·차단·레슨 도메인 계약(백엔드 #603)이 확정되어 플레이스홀더를 실제 화면으로 교체했다.
  {
    path: 'closures',
    element: <VenueClosuresPage />,
    handle: { title: '휴무일·차단시간 설정' },
  },
  // 🚨 #667 — 예약 조회 계약(백엔드 #666)이 확정되어 플레이스홀더를 실제 화면으로 교체했다.
  {
    path: 'bookings',
    element: <VenueBookingsCalendarPage />,
    handle: { title: '예약 캘린더' },
  },
  {
    path: 'bookings/list',
    element: <VenueBookingsListPage />,
    handle: { title: '예약 목록' },
  },
  // `navItems` 미등록 — `courts/:courtId/operating-hours` 처럼 사이드바 밖 하위 라우트(캘린더·목록에서 진입).
  {
    path: 'bookings/:reservationId',
    element: <VenueBookingDetailPage />,
    handle: { title: '예약 상세' },
  },
  // 🚨 #758 — 예약 기능 사용 설정·전환·이력 계약(백엔드 #757)이 확정되어 화면을 연다.
  {
    path: 'reservation-setting',
    element: <VenueReservationSettingPage />,
    handle: { title: '예약 기능 설정' },
  },
  // 🚨 #729 — 공지·홍보 발송 계약(백엔드 #714·#715·#728)이 확정되어 플레이스홀더를 실제 화면으로 교체했다.
  {
    path: 'notices',
    element: <VenueNoticesHomePage />,
    handle: { title: '공지·홍보 관리' },
  },
  // 하위 3화면은 `navItems` 미등록 — `sales`(#704)와 같은 관례로 홈의 카드·버튼에서만 진입한다.
  {
    path: 'notices/new',
    element: <VenueNoticeComposePage />,
    handle: { title: '공지 작성' },
  },
  {
    path: 'notices/history',
    element: <VenueNoticeHistoryPage />,
    handle: { title: '발송 이력' },
  },
  {
    path: 'notices/:campaignId',
    element: <VenueNoticeDetailPage />,
    handle: { title: '공지·홍보 상세' },
  },
  // 🚨 #732 — 지역 홍보 작성·예상 비용·발송 계약(백엔드 #731)이 확정되어 #729가 비활성으로 막아뒀던
  // "새 홍보 작성"을 연다. `notices/new`(NOTICE 전용)와 별도 경로 — `navItems` 미등록, 홈 카드·이력의
  // "이어서 작성" 액션에서만 진입(#729 §자율 결정 7과 같은 관례).
  {
    path: 'notices/promotions/new',
    element: <VenuePromotionComposePage />,
    handle: { title: '지역 홍보 작성' },
  },
  {
    path: 'notices/promotions/:campaignId/edit',
    element: <VenuePromotionComposePage />,
    handle: { title: '지역 홍보 이어서 작성' },
  },
  // 🚨 #738 — 홍보 성과·과금 조회 계약(백엔드 #734·#735·#736)이 확정되어 성과·비용 조회 화면을 연다.
  // `navItems` 미등록 — `notices/history`와 같은 관례로 관리 홈의 링크에서만 진입한다.
  {
    path: 'notices/performance',
    element: <VenuePromotionPerformancePage />,
    handle: { title: '홍보 성과·비용 조회' },
  },
  // 🚨 #704 — 매출·정산·환불 결과 조회 계약(백엔드 #703)이 확정되어 플레이스홀더를 실제 화면으로 교체했다.
  {
    path: 'sales',
    element: <VenueSalesHomePage />,
    handle: { title: '매출·정산' },
  },
  // 하위 4화면은 `navItems` 미등록 — `bookings/:reservationId`처럼 사이드바 밖 하위 라우트(홈의
  // "하위 메뉴 진입" 링크로만 도달한다).
  {
    path: 'sales/revenue',
    element: <VenueSalesRevenuePage />,
    handle: { title: '매출 현황' },
  },
  {
    path: 'sales/cycles',
    element: <VenueSalesCyclesPage />,
    handle: { title: '정산 확정·예정' },
  },
  {
    path: 'sales/refunds',
    element: <VenueSalesRefundsPage />,
    handle: { title: '환불 처리 결과' },
  },
  {
    path: 'sales/deductions',
    element: <VenueSalesDeductionsPage />,
    handle: { title: '분쟁환불 차감 내역' },
  },
]

/** 레지스트리-라우터 동기화 테스트가 참조한다. index 라우트는 빈 문자열로 본다. */
export const VENUE_CHILD_PATHS: string[] = venueChildren.map((route) =>
  route.index === true ? '' : (route.path ?? '')
)

export const appRoutes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <RootRedirect /> },
      {
        path: '/login',
        element: (
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        ),
        handle: { title: '로그인' },
      },
      // 🚨 계정 안내 2개는 **공개 라우트**다 — 로그인하지 못한 사람이 오는 화면이라
      // `GuestRoute`(로그인 상태면 튕겨냄)로도 감싸지 않는다(설계 §2-1(g)).
      {
        path: '/account/password-reset',
        element: <AccountPasswordResetPage />,
        handle: { title: '비밀번호 찾기' },
      },
      {
        path: '/account/signup-guide',
        element: <AccountSignupGuidePage />,
        handle: { title: '구장 관리자 가입 안내' },
      },
      {
        path: '/venues/select',
        element: (
          <ProtectedRoute>
            <VenueSelectPage />
          </ProtectedRoute>
        ),
        handle: { title: '구장 선택' },
      },
      // 🚨 #749 — "새 구장 등록"은 이미 최소 1개 구장을 관리 중인 로그인 상태에서만 쓰인다(설계 §2
      // "진입 경로"). `venueId` 를 아직 모르는 상태에서 여는 화면이라 `/venues/select` 와 같은 층위의
      // 독립 라우트로 둔다 — `venueChildren`(구장별 하위 화면) 밖.
      {
        path: '/venues/register',
        element: (
          <ProtectedRoute>
            <VenueRegisterPage />
          </ProtectedRoute>
        ),
        handle: { title: '새 구장 등록' },
      },
      {
        path: '/venues/register/complete',
        element: (
          <ProtectedRoute>
            <VenueRegisterCompletePage />
          </ProtectedRoute>
        ),
        handle: { title: '구장 등록 완료' },
      },
      {
        path: '/venues/:venueId',
        element: (
          <ProtectedRoute>
            <VenueLayout />
          </ProtectedRoute>
        ),
        children: venueChildren,
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]

export const router = createBrowserRouter(appRoutes)
